import React, { memo, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles, Input, Modal, Switch, Text } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';

import styles from './styles';

const useStyles = createUseStyles(styles, 'CreateViewModal');

const ViewConfirmModal = memo(
    ({
        className,
        labels,
        isOpen,
        onSave,
        onClose,
        canManagePublicViews,
        canManagePrivateViews,
        inputAutoFocus = true,
    }) => {
        let defaultIsPrivateValue = false;
        if (!canManagePublicViews && canManagePrivateViews) defaultIsPrivateValue = true;
        const classes = useStyles();
        const classNames = classnames(classes.root, className);
        const [values, setValues] = useState({ isPrivate: defaultIsPrivateValue });
        const [isLoading, setIsLoading] = useState(false);
        const { t } = useWeb5Context();

        const onChange = useCallback(
            (field) => {
                return (value) => {
                    const newInputValues = { ...values, [field]: value };
                    setValues(newInputValues);
                };
            },
            [values],
        );

        const handleOnSave = useCallback(() => {
            if (!values['name']) return;
            setIsLoading(true);
            onSave &&
                onSave(values).finally(() => {
                    setIsLoading(false);
                    onClose && onClose();
                });
        }, [values, onSave, onClose]);

        const handleOnClose = useCallback(() => {
            if (isLoading) return;
            onClose && onClose();
        }, [onClose, isLoading]);

        const newModalViewName = `${labels?.viewName || t('label_view_name')}*`;

        let canSave = false;
        if (values?.name && values?.name?.trim() !== '') canSave = true;

        const isSwitchDisabled = useMemo(() => {
            if (canManagePublicViews !== canManagePrivateViews) return true;
            return false;
        }, [canManagePublicViews, canManagePrivateViews]);

        return (
            <Modal
                className={classNames}
                overlayClassName={classes.overlay}
                isOpen={isOpen}
                title={labels?.title || t('label_new_view')}
                confirmText={labels?.confirmText || t('action_save')}
                cancelText={labels?.cancelText || t('action_cancel')}
                onConfirm={handleOnSave}
                onCancel={handleOnClose}
                onRequestClose={handleOnClose}
                useCornerClose={false}
                isConfirmDisabled={isLoading || !canSave}
                isConfirmLoading={isLoading}
            >
                <div className={classes.content}>
                    <div className={classes.formContainer}>
                        <div className={classes.inputOne}>
                            <Input
                                label={newModalViewName}
                                placeholder={
                                    labels?.viewNamePlaceholder || t('placeholder_text_field')
                                }
                                onChange={onChange('name')}
                                value={values['name'] || ''}
                                isFullWidth
                                isReadOnly={isLoading}
                                autoFocus={inputAutoFocus}
                            />
                        </div>
                        <div className={classes.inputTwo}>
                            <Text>{labels?.saveAs || t('label_view_public_private_toggle')}</Text>
                            <Switch
                                isDisabled={isLoading || isSwitchDisabled}
                                onChange={onChange('isPrivate')}
                                checked={values['isPrivate']}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        );
    },
);

ViewConfirmModal.propTypes = {
    className: PropTypes.string,
    onSave: PropTypes.func,
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    labels: PropTypes.shape({
        title: PropTypes.string,
        confirmText: PropTypes.string,
        cancelText: PropTypes.string,
        viewName: PropTypes.string,
        saveAs: PropTypes.string,
        viewNamePlaceholder: PropTypes.string,
        saveAsPlaceholder: PropTypes.string,
    }),
};

export default ViewConfirmModal;
